/*  Brax theme — front-end behavior  (build: fix-11.1 cdn-bust 2026-05-29)
 *  Strict, hand-written. No bundler, no framework.
 *  Modules:
 *    - cart      : AJAX add / change / drawer mini state, header count
 *    - hero      : homepage hero slide rotation + dot tablist
 *    - pdp       : variant picker, ?variant= URL state, qty stepper, AJAX add
 *    - mobile    : header drawer toggle, ESC, focus trap, scroll lock
 *    - reveal    : intersection-observer for fh-grid + tile entrance
 *    - newsletter: footer newsletter inline state
 *    - toasts    : single-at-a-time confirmation toast
 *    - faq/specs : accordion toggles
 */
(function () {
  'use strict';
  // BRAX_BASE_BUILD: fix-11-v3 — force a new asset-hash so Shopify CDN
  // serves the latest qty-stepper fix instead of the stale minified
  // version cached under the previous URL hash.
  window.__BRAX_BASE_BUILD__ = 'fix-11-v3';

  // ─── tiny helpers ─────────────────────────────────────────────────
  const $  = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  // Fix #28 — currency-aware money formatter.
  //
  // The active currency symbol is rendered by Liquid into
  // `<html data-currency-symbol="…">` (in layout/theme.liquid)
  // because Shopify Markets converts prices to the visitor's
  // active currency but the moneyFormat string in Shopify Admin
  // hardcodes "$". Reading the symbol from the DOM lets JS stay
  // in sync with Liquid's render — including after a variant
  // change or cart-quantity change, which is when JS re-renders
  // a price and used to overwrite the correct symbol with "$".
  //
  // Number formatting uses Intl.NumberFormat for:
  //   - locale-aware thousands + decimal separators (€1.234,56
  //     for EU locales, $1,234.56 for US)
  //   - matches `money_without_trailing_zeros` behavior — strip
  //     "$349.00" → "$349" when amount is an integer.
  function fmtMoney(cents) {
    const html = document.documentElement;
    const sym = (html && html.dataset.currencySymbol) || '$';
    const num = Math.round(cents) / 100;
    const isInt = num % 1 === 0;
    const locale = (html && html.lang) || navigator.language || 'en';
    const numStr = num.toLocaleString(locale, {
      minimumFractionDigits: isInt ? 0 : 2,
      maximumFractionDigits: 2,
    });
    return sym + numStr;
  }
  function dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  }

  // ─── toasts ───────────────────────────────────────────────────────
  let toastEl = null;
  function toast(message, kind) {
    if (toastEl) toastEl.remove();
    const t = document.createElement('div');
    t.className = 'bx-toast ' + (kind || 'safe');
    t.setAttribute('role', 'status');
    t.textContent = message;
    document.body.appendChild(t);
    toastEl = t;
    requestAnimationFrame(() => t.classList.add('in'));
    setTimeout(() => {
      t.classList.remove('in');
      setTimeout(() => { if (t.parentNode) t.remove(); if (toastEl === t) toastEl = null; }, 240);
    }, 4000);
  }

  // ─── cart count badge ─────────────────────────────────────────────
  // Only target real badges — explicitly EXCLUDE the <html> element which
  // carries data-cart-count as a global state hook. Setting its textContent
  // would wipe the entire page body.
  function setCartCount(n) {
    $$('.cart-count, [data-cart-count]').forEach((el) => {
      if (el === document.documentElement || el === document.body) return;
      el.textContent = n;
      el.style.display = n > 0 ? '' : 'none';
    });
    document.documentElement.dataset.cartCount = String(n);
  }

  async function refreshCartCount() {
    try {
      const r = await fetch('/cart.js', { headers: { accept: 'application/json' } });
      const c = await r.json();
      setCartCount(c.item_count);
      return c;
    } catch (e) { return null; }
  }

  // ─── add to cart (form or button[data-add-to-cart]) ───────────────
  async function addToCart(variantId, qty, properties) {
    const body = { items: [{ id: Number(variantId), quantity: Number(qty) || 1 }] };
    if (properties) body.items[0].properties = properties;
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      let msg = 'Add to cart failed';
      try { const j = await r.json(); if (j && j.description) msg = j.description; } catch (e) {}
      throw new Error(msg);
    }
    return await r.json();
  }

  async function changeLine(line, quantity) {
    const r = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Number(quantity) }),
    });
    if (!r.ok) throw new Error('Cart change failed');
    return await r.json();
  }

  // ─── PDP behavior ─────────────────────────────────────────────────
  function initPdp() {
    const root = $('[data-pdp]');
    if (!root) return;

    const variantsRaw = root.getAttribute('data-variants');
    let VARIANTS = [];
    try { VARIANTS = JSON.parse(variantsRaw || '[]'); } catch (e) { VARIANTS = []; }

    // selected option index per option index (0-based)
    const optionCount = Number(root.getAttribute('data-option-count') || 0);
    const selected = new Array(optionCount).fill(null);

    // Try to pre-select from URL ?variant=
    const urlVariantId = new URLSearchParams(window.location.search).get('variant');
    let initial = null;
    if (urlVariantId) initial = VARIANTS.find((v) => String(v.id) === String(urlVariantId));
    if (!initial) initial = VARIANTS.find((v) => v.available) || VARIANTS[0];

    if (initial) {
      for (let i = 0; i < optionCount; i++) selected[i] = initial['option' + (i + 1)] || null;
    }

    function findVariant() {
      return VARIANTS.find((v) => {
        for (let i = 0; i < optionCount; i++) {
          if ((v['option' + (i + 1)] || null) !== (selected[i] || null)) return false;
        }
        return true;
      });
    }

    function paintAvailability() {
      $$('[data-opt-button]', root).forEach((btn) => {
        const oi = Number(btn.getAttribute('data-option-index'));
        const val = btn.getAttribute('data-option-value');
        // available if there's any variant that has this value in this position
        // and matches the other currently-selected options
        const hit = VARIANTS.find((v) => {
          if ((v['option' + (oi + 1)] || null) !== val) return false;
          for (let i = 0; i < optionCount; i++) {
            if (i === oi) continue;
            if (selected[i] == null) continue;
            if ((v['option' + (i + 1)] || null) !== selected[i]) return false;
          }
          return true;
        });
        btn.classList.toggle('on', selected[oi] === val);
        btn.classList.toggle('unavail', !hit || hit.available === false);
      });
    }

    function paintBuy() {
      const v = findVariant();
      const addBtn = $('[data-add-to-cart]', root);
      const priceMain = $('[data-price-main]', root);
      const priceCompare = $('[data-price-compare]', root);
      const priceSave = $('[data-price-save]', root);

      if (!v) {
        if (addBtn) {
          addBtn.disabled = true;
          addBtn.textContent = addBtn.getAttribute('data-unavailable-label') || 'Unavailable';
        }
        return;
      }
      // URL state
      const sp = new URLSearchParams(window.location.search);
      sp.set('variant', String(v.id));
      window.history.replaceState({}, '', window.location.pathname + '?' + sp.toString());

      if (priceMain) priceMain.textContent = fmtMoney(v.price);
      if (priceCompare) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          priceCompare.style.display = '';
          priceCompare.textContent = fmtMoney(v.compare_at_price);
          if (priceSave) {
            priceSave.style.display = '';
            priceSave.textContent = (priceSave.getAttribute('data-save-prefix') || 'SAVE ') + fmtMoney(v.compare_at_price - v.price);
          }
        } else {
          priceCompare.style.display = 'none';
          if (priceSave) priceSave.style.display = 'none';
        }
      }

      if (addBtn) {
        if (v.available) {
          addBtn.disabled = false;
          const qty = Number(($('[data-pdp-qty] input', root) || {}).value || 1);
          const total = (v.price * qty);
          const tmpl = addBtn.getAttribute('data-label-template') || 'Add to cart · {total}';
          addBtn.textContent = tmpl.replace('{total}', fmtMoney(total));
          addBtn.setAttribute('data-variant-id', String(v.id));
        } else {
          addBtn.disabled = true;
          addBtn.textContent = addBtn.getAttribute('data-soldout-label') || 'Sold out';
        }
      }
    }

    // Reset the Add-to-Cart button out of its "Go to checkout" state
    // when the user touches a variant picker or qty stepper. Called by
    // those handlers below so the button morphs back to idle automatically.
    function resetCheckoutState() {
      const btn = $('[data-add-to-cart]', root);
      if (!btn) return;
      if (btn.getAttribute('data-state') === 'checkout') {
        btn.removeAttribute('data-state');
        btn.classList.remove('is-checkout');
      }
    }

    // wire option buttons
    $$('[data-opt-button]', root).forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (btn.classList.contains('unavail')) return;
        const oi = Number(btn.getAttribute('data-option-index'));
        const val = btn.getAttribute('data-option-value');
        selected[oi] = val;
        resetCheckoutState();
        paintAvailability();
        paintBuy();
      });
    });

    // qty stepper. CRITICAL Fix #11 v2: scope tightly to [data-delta]
    // ONLY — never to plain 'button' inside qtyWrap. The Add-to-Cart
    // submit button lives in the same wrapper and would otherwise have
    // resetCheckoutState() called on every click, wiping data-state
    // before the form-submit handler can read it. Tag for the next
    // time this confuses someone: BRAX_QTY_DATA_DELTA_SCOPED_V2.
    const qtyWrap = $('[data-pdp-qty]', root);
    if (qtyWrap) {
      const stepperInput = $('input', qtyWrap);
      const stepperBtns = $$('[data-delta]', qtyWrap);
      stepperBtns.forEach((b) => {
        b.addEventListener('click', () => {
          const cur = Math.max(1, Number(stepperInput.value) || 1);
          const delta = Number(b.getAttribute('data-delta') || 0);
          stepperInput.value = String(Math.max(1, cur + delta));
          resetCheckoutState();
          paintBuy();
        });
      });
    }

    // thumb rail
    $$('[data-thumb]', root).forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const idx = thumb.getAttribute('data-thumb');
        $$('[data-thumb]', root).forEach((t) => t.classList.toggle('active', t === thumb));
        $$('[data-main-image]', root).forEach((img) => {
          img.classList.toggle('on', img.getAttribute('data-main-image') === idx);
        });
        const pin = $('[data-thumb-pin]', root);
        if (pin) pin.textContent = thumb.getAttribute('data-pin') || pin.textContent;
      });
    });

    // submit — 4-state machine: idle → adding → added (checkout) → on next click, navigate to /checkout
    const form = $('form[action="/cart/add"]', root);
    if (form) {
      form.addEventListener('submit', async (e) => {
        const addBtn = $('[data-add-to-cart]', root);

        // STATE 3 → second submit after a successful add: route to checkout.
        // Important: do NOT preventDefault here. We want the browser to
        // handle navigation cleanly. We replace the form's action+method
        // intent by setting location.assign() and stopping further work.
        if (addBtn && addBtn.getAttribute('data-state') === 'checkout') {
          e.preventDefault();
          window.location.assign('/checkout');
          return;
        }

        e.preventDefault();
        if (!addBtn || addBtn.disabled) return;

        const variantId = addBtn.getAttribute('data-variant-id') || ($('input[name="id"]', form) || {}).value;
        const qty = Number(($('[data-pdp-qty] input', root) || {}).value || 1);
        const idleText = addBtn.textContent;

        // STATE 2 → in-flight
        addBtn.disabled = true;
        addBtn.textContent = addBtn.getAttribute('data-adding-label') || 'Adding…';

        try {
          await addToCart(variantId, qty);
          await refreshCartCount();
          // STATE 3 → success: morph button into the checkout CTA. Re-enable
          // so a second click can navigate. The `data-state="checkout"`
          // attribute is the source of truth that the next submit reads.
          addBtn.disabled = false;
          addBtn.setAttribute('data-state', 'checkout');
          addBtn.classList.add('is-checkout');
          addBtn.textContent = addBtn.getAttribute('data-checkout-label') || 'Go to checkout →';
        } catch (err) {
          // STATE 4 → error: revert to idle, surface message.
          addBtn.disabled = false;
          addBtn.textContent = idleText;
          toast(err.message || 'Add to cart failed', 'risky');
        }
      });
    }

    // initial paint
    paintAvailability();
    paintBuy();
  }

  // ─── cart page (qty steppers & remove) ────────────────────────────
  function initCart() {
    const root = $('[data-cart-page]');
    if (!root) return;

    async function refresh() {
      try {
        const c = await fetch('/cart.js', { headers: { accept: 'application/json' } }).then((r) => r.json());
        // server-rendered totals — update the visible ones
        const sub = $('[data-cart-subtotal]', root);
        if (sub) sub.textContent = fmtMoney(c.items_subtotal_price);
        setCartCount(c.item_count);
        if (c.item_count === 0) {
          window.location.reload();
        }
      } catch (e) { /* swallow */ }
    }

    $$('[data-line-row]', root).forEach((row) => {
      const line = row.getAttribute('data-line');
      const totalEl = $('[data-line-total]', row);
      const variantId = row.getAttribute('data-variant');
      const unitCents = Number(row.getAttribute('data-unit-cents') || 0);

      $$('[data-qty-delta]', row).forEach((b) => {
        b.addEventListener('click', async () => {
          const input = $('[data-qty-input]', row);
          const cur = Math.max(0, Number(input.value) || 0);
          const next = Math.max(0, cur + Number(b.getAttribute('data-qty-delta') || 0));
          input.value = String(next);
          if (totalEl && unitCents) totalEl.textContent = fmtMoney(unitCents * next);
          try {
            await changeLine(line, next);
            refresh();
          } catch (err) {
            toast(err.message || 'Update failed', 'risky');
          }
        });
      });

      const trash = $('[data-line-remove]', row);
      if (trash) {
        trash.addEventListener('click', async () => {
          try {
            await changeLine(line, 0);
            row.remove();
            refresh();
          } catch (err) {
            toast(err.message || 'Remove failed', 'risky');
          }
        });
      }
    });
  }

  // ─── mobile drawer (header) ───────────────────────────────────────
  function initMobileDrawer() {
    const toggle = $('[data-mobile-toggle]');
    const drawer = $('[data-mobile-drawer]');
    const closeBtn = $('[data-mobile-close]');
    if (!toggle || !drawer) return;

    let prevBodyOverflow = '';
    function open() {
      prevBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      drawer.classList.add('open');
      drawer.setAttribute('aria-hidden', 'false');
      const first = $('a, button', drawer);
      if (first) first.focus();
    }
    function close() {
      document.body.style.overflow = prevBodyOverflow;
      drawer.classList.remove('open');
      drawer.setAttribute('aria-hidden', 'true');
      toggle.focus();
    }
    toggle.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    drawer.addEventListener('click', (e) => { if (e.target === drawer) close(); });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('open')) close();
    });
  }

  // ─── header search no-op tooltip ──────────────────────────────────
  function initHeaderSearch() {
    const btn = $('[data-header-search]');
    if (!btn) return;
    let bubble = null;
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      if (bubble) { bubble.remove(); bubble = null; return; }
      bubble = document.createElement('span');
      bubble.className = 'bx-tooltip';
      bubble.textContent = btn.getAttribute('data-tooltip') || 'Search coming soon';
      btn.appendChild(bubble);
      setTimeout(() => { if (bubble) { bubble.remove(); bubble = null; } }, 2200);
    });
  }

  // ─── accordion (faq, specs) ───────────────────────────────────────
  function initAccordions() {
    $$('[data-accordion]').forEach((root) => {
      $$('[data-acc-head]', root).forEach((head) => {
        head.addEventListener('click', () => {
          const item = head.closest('[data-acc-item]');
          if (!item) return;
          const open = item.classList.toggle('open');
          item.setAttribute('aria-expanded', open ? 'true' : 'false');
          const tog = $('.toggle', head);
          if (tog) tog.textContent = open ? '−' : '+';
        });
      });
    });
  }

  // ─── newsletter inline state ──────────────────────────────────────
  function initNewsletter() {
    $$('form[data-newsletter]').forEach((form) => {
      const btn = $('button[type="submit"], .nl-btn', form);
      // Standard Shopify form submits to /contact#newsletter — let it through
      // but mark state.
      form.addEventListener('submit', () => {
        if (btn) {
          btn.disabled = true;
          btn.textContent = btn.getAttribute('data-submitting-label') || 'Submitting…';
        }
      });
    });
  }

  // ─── account orders view toggle ───────────────────────────────────
  function initOrdersView() {
    const root = $('[data-orders-page]');
    if (!root) return;
    $$('[data-view]', root).forEach((b) => {
      b.addEventListener('click', () => {
        const v = b.getAttribute('data-view');
        root.setAttribute('data-view-mode', v);
        $$('[data-view]', root).forEach((x) => x.classList.toggle('on', x === b));
      });
    });
  }

  // ─── Fix #30 — PDP Protection / Feed carousels ───────────────────
  // Shared chrome: a horizontal-scroll track with prev/next arrows
  // pinned to a left aside. Arrows scroll the track by one card-width
  // via requestAnimationFrame and disable at the track's start/end.
  function initPcCarousels(scope) {
    $$('[data-pc]', scope || document).forEach((root) => {
      const track = $('[data-pc-track]', root);
      const prev  = $('[data-pc-prev]', root);
      const next  = $('[data-pc-next]', root);
      if (!track || !prev || !next) return;
      if (root.__bxPc) return;                 // re-init (theme editor) must not double-bind
      root.__bxPc = true;

      const cardStep = () => {
        const card = $('.pc-card', track) || track.firstElementChild;
        const gap = parseFloat(getComputedStyle(track).gap) || 18;
        return card ? card.getBoundingClientRect().width + gap : 320;
      };

      const updateArrows = () => {
        const max = track.scrollWidth - track.clientWidth - 1;
        prev.disabled = track.scrollLeft <= 0;
        next.disabled = track.scrollLeft >= max;
      };

      const animateScroll = (target) => {
        const start = track.scrollLeft;
        const dist = target - start;
        if (Math.abs(dist) < 1) return;
        const t0 = performance.now();
        const dur = 380;
        const ease = (t) => 1 - Math.pow(1 - t, 3);
        const tick = (now) => {
          const k = Math.min(1, (now - t0) / dur);
          track.scrollLeft = start + dist * ease(k);
          if (k < 1) requestAnimationFrame(tick);
          else updateArrows();
        };
        requestAnimationFrame(tick);
      };

      prev.addEventListener('click', () => animateScroll(track.scrollLeft - cardStep()));
      next.addEventListener('click', () => animateScroll(track.scrollLeft + cardStep()));
      track.addEventListener('scroll', () => requestAnimationFrame(updateArrows), { passive: true });
      window.addEventListener('resize', updateArrows);
      updateArrows();
    });
  }

  // ─── home hero carousel ───────────────────────────────────────────
  // Up to four merchant-managed slides (section blocks). A slide holding an
  // uploaded video runs for exactly as long as that video and advances when it
  // ends; every other slide runs on the editor's timer. All slides are in the DOM
  // and CSS cross-fades them; this only toggles classes, so Liquid keeps
  // owning the markup (money formatting, video tags, the editor's per-block
  // outlines) and there is no JSON payload to keep in sync.
  //
  // The prototype's prev/next arrows are deliberately gone, so the dots are
  // the only control — which means they owe the visitor the keyboard model
  // the arrows used to provide. They are a tablist with roving tabindex and
  // Left/Right/Home/End.
  const HERO_MIN_INTERVAL = 1200;

  function initHero(scope) {
    $$('[data-hero]', scope || document).forEach((root) => {
      // Destroy-then-init rather than the "already bound, skip" guard the pc
      // carousels use: this module owns a timer and document-level listeners,
      // and the theme editor throws the old nodes away without telling the
      // old closure to stop firing show() at them.
      if (root.__bxHero) root.__bxHero.destroy();

      const slides = $$('[data-hero-slide]', root);
      const dots   = $$('[data-hero-dot]', root);
      const tabs   = $('[data-hero-dots]', root);
      if (!slides.length) return;

      const reduced  = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      const interval = Number(root.getAttribute('data-hero-interval')) || 0;
      const wants    = root.getAttribute('data-hero-autoplay') !== 'false';
      const nativeInert = 'inert' in HTMLElement.prototype;
      const canRotate = slides.length > 1 && wants && interval >= HERO_MIN_INTERVAL && !reduced;

      let index = Math.max(0, slides.findIndex((el) => el.classList.contains('on')));
      let timer = null;
      let startedAt = 0;
      let remaining = interval;
      let stopped = !canRotate;
      // Reasons overlap freely — the tab can be hidden while the pointer is
      // still over the hero — so a boolean would resume on the first unhover.
      const paused = new Set();

      const fillOf = (i) => (dots[i] ? $('.fill', dots[i]) : null);

      // The visible video on the current slide. A slide can carry a desktop and
      // a mobile cut with CSS hiding one, so pick by what is actually laid out.
      function activeVideo() {
        const slide = slides[index];
        if (!slide) return null;
        const vids = $$('video', slide);
        for (let k = 0; k < vids.length; k++) if (vids[k].offsetParent !== null) return vids[k];
        return vids[0] || null;
      }
      function videoMs(v) {
        return (v && v.duration && isFinite(v.duration) && v.duration > 0) ? v.duration * 1000 : 0;
      }
      function setFillDuration(ms) {
        const fill = fillOf(index);
        if (fill && ms > 0) fill.style.animationDuration = ms + 'ms';
      }

      // CSS will not restart an animation that is already running. React did
      // this by remounting the node; here we drop the animation NAME, force a
      // style flush, then hand the name back to the stylesheet. Toggle the
      // name, never the `animation` shorthand — the shorthand would also wipe
      // the duration the section set via --hero-duration.
      function restartFill() {
        const fill = fillOf(index);
        if (!fill) return;
        fill.style.animationName = 'none';
        if (stopped) return;
        void fill.offsetWidth;
        fill.style.animationName = '';
        fill.style.animationPlayState = paused.size ? 'paused' : 'running';
      }

      // Only the slide on screen plays. A slide can hold two <video> elements
      // (desktop + mobile, one hidden per breakpoint) and a display:none video
      // still occupies a hardware decoder, hence the offsetParent check.
      function paintMedia() {
        slides.forEach((slide, i) => {
          $$('video', slide).forEach((v) => {
            if (i !== index || paused.size || v.offsetParent === null) {
              try { v.pause(); } catch (e) { /* not ready yet */ }
              return;
            }
            v.muted = true;                       // iOS refuses to autoplay otherwise
            const played = v.play();
            if (played && played.catch) played.catch(() => {});
          });
        });
      }

      function clear() { if (timer) { clearTimeout(timer); timer = null; } }

      function arm(ms) {
        clear();
        if (typeof ms === 'number') remaining = Math.max(0, ms);
        if (stopped || paused.size) return;
        startedAt = Date.now();
        timer = setTimeout(() => show(index + 1), remaining);
      }

      // A slide with an uploaded video is timed by that video: `ended` advances
      // it and the timer below is only a backstop for a video that stalls or
      // never fires. Everything else -- static images, YouTube facades, empty
      // panels -- uses the editor's "seconds per slide".
      function armForSlide() {
        clear();
        const v = activeVideo();
        if (v && slides.length > 1) {
          const total = videoMs(v);
          if (total) {
            const left = Math.max(0, total - (v.currentTime * 1000));
            setFillDuration(total);
            arm(left + 400);                     // backstop, just past the end
          } else {
            // Metadata has not arrived yet; loadedmetadata re-arms us.
            setFillDuration(interval);
            arm(interval);
          }
          return;
        }
        setFillDuration(interval);
        arm(interval);
      }

      function show(next) {
        const n = slides.length;
        index = ((next % n) + n) % n;
        slides.forEach((slide, i) => {
          const on = i === index;
          slide.classList.toggle('on', on);
          slide.setAttribute('aria-hidden', on ? 'false' : 'true');
          // inert keeps the off-slides' links out of the tab order and out of
          // the screen-reader buffer while they are still painted for the fade.
          if (on) slide.removeAttribute('inert'); else slide.setAttribute('inert', '');
          if (!nativeInert) {
            $$('a, button', slide).forEach((el) => {
              if (on) el.removeAttribute('tabindex'); else el.setAttribute('tabindex', '-1');
            });
          }
        });
        dots.forEach((dot, i) => {
          const on = i === index;
          dot.classList.toggle('on', on);
          dot.setAttribute('aria-selected', on ? 'true' : 'false');
          dot.tabIndex = on ? 0 : -1;             // one tab stop for the whole tablist
        });
        // Always from the beginning: a returning slide should replay, not
        // resume wherever it happened to be left.
        const vid = activeVideo();
        if (vid) {
          vid.loop = slides.length <= 1;          // looping would never fire 'ended'
          try { vid.currentTime = 0; } catch (e) { /* not seekable yet */ }
        }
        paintMedia();
        armForSlide();   // sets the bar's duration...
        restartFill();   // ...then restarts it with that duration
      }

      // freeze/thaw fire only on the 0<->1 edge of `paused`, and freeze banks
      // the time already served so resuming does not restart the leg.
      function freeze() {
        if (stopped) return;
        if (timer) remaining = Math.max(0, remaining - (Date.now() - startedAt));
        clear();
        const fill = fillOf(index);
        if (fill) fill.style.animationPlayState = 'paused';
      }
      function thaw() {
        paintMedia();
        if (stopped) return;
        const fill = fillOf(index);
        if (fill) fill.style.animationPlayState = 'running';
        if (activeVideo()) armForSlide(); else arm();
      }
      function hold(reason) {
        if (paused.has(reason)) return;
        paused.add(reason);
        if (paused.size === 1) freeze();
        if (reason === 'hidden') paintMedia();
      }
      function release(reason) {
        if (!paused.delete(reason)) return;
        if (paused.size === 0) thaw(); else if (reason === 'hidden') paintMedia();
      }

      // Picking a slide restarts the clock on the one you chose rather than
      // halting the carousel -- otherwise the hero sits on that slide until
      // something else is clicked.
      function pick(i) { show(i); }

      dots.forEach((dot, i) => dot.addEventListener('click', () => pick(i)));

      if (tabs) tabs.addEventListener('keydown', (e) => {
        const n = dots.length;
        let to = -1;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') to = (index + 1) % n;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') to = (index - 1 + n) % n;
        else if (e.key === 'Home') to = 0;
        else if (e.key === 'End') to = n - 1;
        else return;
        e.preventDefault();
        pick(to);
        if (dots[to]) dots[to].focus();
      });

      // Hovering deliberately does NOT pause: the bar stalling under the
      // pointer read as a bug rather than a courtesy. Keyboard focus still
      // pauses, so anyone reading the slide with the keyboard keeps control.
      root.addEventListener('focusin', () => hold('focus'));
      root.addEventListener('focusout', (e) => { if (!root.contains(e.relatedTarget)) release('focus'); });

      // ── YouTube slides ──────────────────────────────────────────
      // Each slide's play button is a facade; the iframe is built here, in a
      // <dialog> shown with showModal() so the top layer supplies the
      // backdrop, Escape and focus trapping. The rotation holds while the
      // player is open, and the iframe is destroyed on close so playback
      // actually stops rather than continuing behind the page.
      const box = $('[data-hero-lightbox]', root);
      const boxFrame = box && $('[data-hero-lightbox-frame]', box);
      const boxOut = box && $('[data-hero-lightbox-out]', box);
      let lastPlay = null;

      // YouTube serves a 120x90 grey placeholder when a video has no
      // maxresdefault, so fall back to the still that always exists.
      $$('[data-hero-yt] img[data-hero-yt-fallback]', root).forEach((im) => {
        const swap = () => {
          const alt = im.getAttribute('data-hero-yt-fallback');
          if (alt && im.src !== alt) im.src = alt;
        };
        im.addEventListener('error', swap);
        const check = () => { if (im.naturalWidth && im.naturalWidth <= 121) swap(); };
        if (im.complete) check(); else im.addEventListener('load', check, { once: true });
      });

      // Teardown is driven from every close path rather than from the dialog's
      // own 'close' event: that event does not fire in every engine that
      // otherwise supports showModal(), and a missed teardown leaves the
      // iframe alive and the audio playing behind the page. Idempotent, so
      // running twice when 'close' does fire costs nothing.
      function closeVideo() {
        if (!box) return;
        if (boxFrame) boxFrame.innerHTML = '';       // kills playback
        release('modal');
        if (box.open) box.close();
        if (lastPlay && document.contains(lastPlay)) lastPlay.focus();
        lastPlay = null;
      }

      function openVideo(btn) {
        const id = btn.getAttribute('data-hero-yt');
        if (!id) return;
        // No <dialog> support: hand the visitor to YouTube rather than
        // trapping them behind a modal that will not open.
        if (!box || typeof box.showModal !== 'function') {
          window.open('https://www.youtube.com/watch?v=' + encodeURIComponent(id),
            '_blank', 'noopener,noreferrer');
          return;
        }
        lastPlay = btn;
        hold('modal');
        if (boxOut) boxOut.href = 'https://www.youtube.com/watch?v=' + encodeURIComponent(id);
        const frame = document.createElement('iframe');
        frame.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) +
                    '?autoplay=1&rel=0';
        frame.title = btn.getAttribute('data-hero-yt-title') || 'Video';
        frame.setAttribute('allow',
          'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
        frame.setAttribute('allowfullscreen', '');   // the player's own fullscreen button
        frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
        boxFrame.innerHTML = '';
        boxFrame.appendChild(frame);
        box.showModal();
      }

      $$('[data-hero-yt]', root).forEach((btn) => {
        btn.addEventListener('click', (e) => { e.preventDefault(); openVideo(btn); });
      });
      if (box) {
        const closeBtn = $('[data-hero-lightbox-close]', box);
        if (closeBtn) closeBtn.addEventListener('click', closeVideo);
        // Clicking the backdrop lands on the dialog itself, not its children.
        box.addEventListener('click', (e) => { if (e.target === box) closeVideo(); });
        // Escape closes the dialog natively; this runs first and tears down.
        box.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeVideo(); });
        // Safety net for engines that do dispatch these.
        box.addEventListener('close', closeVideo);
        box.addEventListener('cancel', closeVideo);
      }

      $$('video', root).forEach((v) => {
        // 'ended' is the real clock for a video slide.
        v.addEventListener('ended', () => {
          if (slides.length > 1 && !stopped && !paused.size && v === activeVideo()) show(index + 1);
        });
        // Duration is unknown until metadata lands; re-arm once it does so the
        // slide and its progress bar match the footage.
        v.addEventListener('loadedmetadata', () => {
          if (v !== activeVideo()) return;
          armForSlide();
          restartFill();
        });
        // A video that cannot load must not strand the carousel on its slide.
        v.addEventListener('error', () => {
          if (v === activeVideo()) { setFillDuration(interval); arm(interval); }
        });
      });

      const onVisibility = () => { if (document.hidden) hold('hidden'); else release('hidden'); };
      const onBlockSelect = (e) => {
        if (!root.contains(e.target)) return;
        hold('editor');
        const i = slides.indexOf(e.target.closest('[data-hero-slide]'));
        if (i >= 0) show(i);
      };
      const onBlockDeselect = (e) => { if (root.contains(e.target)) release('editor'); };

      document.addEventListener('visibilitychange', onVisibility);
      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', onBlockSelect);
        document.addEventListener('shopify:block:deselect', onBlockDeselect);
      }

      // Listeners bound to elements die with the nodes; the document-level
      // ones and the timer do not, so teardown has to be explicit.
      root.__bxHero = {
        destroy() {
          clear();
          if (box && box.open) box.close();
          document.removeEventListener('visibilitychange', onVisibility);
          document.removeEventListener('shopify:block:select', onBlockSelect);
          document.removeEventListener('shopify:block:deselect', onBlockDeselect);
          root.__bxHero = null;
        }
      };

      root.setAttribute('data-hero-rotating', canRotate ? 'true' : 'false');
      show(index);
    });
  }

  // ─── Fix #30 — PDP Feed (click thumbnail → swap to autoplay embed) ─
  // ─── newsroom listing: category pills + search filter ──────────────
  function initNewsroomFilter() {
    const root = document.querySelector('[data-nr-root]');
    if (!root) return;
    const listEl   = root.querySelector('[data-nr-list]');
    const emptyEl  = root.querySelector('[data-nr-empty]');
    const searchEl = root.querySelector('[data-nr-search]');
    const clearEl  = root.querySelector('[data-nr-search-clear]');
    const pills    = [...root.querySelectorAll('[data-nr-pill]')];
    const resetEl  = root.querySelector('[data-nr-reset]');
    const items    = [...listEl.querySelectorAll('.nr-feature')];

    let currentTag = '__all';
    let currentQ   = '';

    function apply() {
      let visible = 0;
      for (const it of items) {
        const tags  = (it.getAttribute('data-nr-tags') || '').toLowerCase();
        const title = (it.getAttribute('data-nr-title') || '').toLowerCase();
        const matchTag = currentTag === '__all' || tags.includes('|' + currentTag.toLowerCase() + '|') || tags.includes('|' + currentTag.toLowerCase());
        const matchQ   = !currentQ || title.includes(currentQ);
        const show = matchTag && matchQ;
        it.style.display = show ? '' : 'none';
        if (show) visible++;
      }
      // Empty state only makes sense when the user has actively filtered
      // items out. If there were zero items to begin with, the merchant
      // hasn't published any articles yet — the empty state's language
      // ("clear filters") would be misleading, so we leave it hidden.
      const filtered = currentTag !== '__all' || currentQ !== '';
      if (emptyEl) emptyEl.hidden = !(filtered && visible === 0);
      if (clearEl) clearEl.hidden = !currentQ;
    }

    pills.forEach(p => p.addEventListener('click', () => {
      pills.forEach(x => x.classList.toggle('active', x === p));
      currentTag = p.getAttribute('data-nr-pill');
      apply();
    }));

    if (searchEl) {
      searchEl.addEventListener('input', () => {
        currentQ = searchEl.value.trim().toLowerCase();
        apply();
      });
    }
    if (clearEl) {
      clearEl.addEventListener('click', () => {
        if (searchEl) searchEl.value = '';
        currentQ = '';
        apply();
      });
    }
    if (resetEl) {
      resetEl.addEventListener('click', () => {
        currentTag = '__all'; currentQ = '';
        if (searchEl) searchEl.value = '';
        pills.forEach(x => x.classList.toggle('active', x.getAttribute('data-nr-pill') === '__all'));
        apply();
      });
    }
  }

  // ─── article: auto-build TOC from H2s + scroll-spy active state ────
  function initArticleToc() {
    const body = document.querySelector('[data-nr-body]');
    const nav  = document.querySelector('[data-nr-toc]');
    if (!body || !nav) return;

    const h2s = [...body.querySelectorAll('h2')];
    if (h2s.length === 0) {
      // no headings → hide the aside entirely
      const aside = document.querySelector('[data-nr-aside]');
      if (aside) aside.style.display = 'none';
      return;
    }

    // Assign ids + link out
    h2s.forEach((h, i) => {
      if (!h.id) {
        const slug = (h.textContent || '')
          .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        h.id = `sec-${i + 1}-${slug}`;
      }
      const a = document.createElement('a');
      a.href = '#' + h.id;
      a.className = i === 0 ? 'active' : '';
      a.innerHTML =
        `<span class="num">${String(i + 1).padStart(2, '0')}</span><span>${h.textContent}</span>`;
      nav.appendChild(a);
    });

    // Simple scroll-spy: set active based on nearest H2 above viewport top+120px
    const links = [...nav.querySelectorAll('a')];
    function spy() {
      const y = window.scrollY + 140;
      let active = 0;
      h2s.forEach((h, i) => { if (h.offsetTop <= y) active = i; });
      links.forEach((l, i) => l.classList.toggle('active', i === active));
    }
    window.addEventListener('scroll', spy, { passive: true });
    spy();
  }

  function initFeedThumbs(scope) {
    $$('[data-feed-media]', scope || document).forEach((media) => {
      const btn = $('[data-feed-play]', media);
      if (!btn || media.__bxFeed) return;
      media.__bxFeed = true;
      btn.addEventListener('click', () => {
        const yt = media.getAttribute('data-yt');
        if (!yt) return;
        const iframe = document.createElement('iframe');
        iframe.src = 'https://www.youtube-nocookie.com/embed/' + yt + '?autoplay=1&rel=0';
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('loading', 'lazy');
        iframe.title = btn.getAttribute('aria-label') || 'YouTube video';
        media.innerHTML = '';
        media.appendChild(iframe);
      });
    });
  }

  // ─── boot ─────────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', () => {
    refreshCartCount();
    initPdp();
    initCart();
    initMobileDrawer();
    initHeaderSearch();
    initAccordions();
    initNewsletter();
    initOrdersView();
    initPcCarousels();
    initHero();
    initFeedThumbs();
    initNewsroomFilter();
    initArticleToc();
  });

  // The theme editor swaps a section's markup in place without reloading the
  // page, which leaves the new nodes unbound. Both modules below are scoped
  // and guarded, so re-running them for just that section is safe.
  document.addEventListener('shopify:section:load', (e) => {
    initPcCarousels(e.target);
    initFeedThumbs(e.target);
    initHero(e.target);
  });

  // The hero owns a timer and document-level listeners, so a section the
  // editor removes has to be told to stop — otherwise its setTimeout keeps
  // calling show() on nodes that left the document.
  document.addEventListener('shopify:section:unload', (e) => {
    $$('[data-hero]', e.target).forEach((el) => { if (el.__bxHero) el.__bxHero.destroy(); });
  });

  // expose for inline handlers
  window.Brax = { addToCart, changeLine, refreshCartCount, toast };
})();
